import _ from "lodash";
import Accessor from "./Accessor";
import ZArray from "./ZArray";
import GFunc from "./__GFunc";

class AuthX {

  /**
   * @typedef {import("../../__Typedef/SYSAuth").authtree} authtree
   * @typedef {import("../../__Typedef/SYSAuth").authctrlset} authctrlset
   */
  /**
   * 
   * @param {String | String[]} permission 
   * @param {"System"} scope 
   */
  static HasPermission(permission, scope = "System"){
    switch(scope){
      default: case "System": return this.HasSYSPermission(permission);
    }
  }

  static HasSYSPermission(permission){
    let authority = GFunc.GetUserSYSAuth();
    if(!authority) return false;
    if(_.isEmpty(permission)) return true;
    return this.CheckPermission(authority, permission);
  }

  /**
   * Ensure the authority tree (authtree) is bounded to the base authority tree (refTree) set, 
   *  i.e. no excess / non-existing authority grant
   * @param {authtree} refTree 
   * @param {authtree} authtree 
   * @returns 
   */
   static Ensure(refTree, authtree){
    let ctrl = this.AuthTree2Ctrl(authtree);
    let tree = this.Ctrl2AuthTree(refTree, ctrl);
    return tree;
  }

  /**
   * check whether the authority tree has the authority node,
   * permission is the dot presentation of the path-to-node
   * @param {authtree} authtree 
   * @param {String | String[]} permission 
   * @returns 
   */
  static CheckPermission(authtree, permission){
    let ctrl = this.AuthTree2Ctrl(authtree);
    permission = ZArray.Ensure(permission);
    for(let i=0; i<permission.length; i++){
      if(ctrl[permission[i]]) return true;
    }
    return false;
  }

  /**
   * Union all authority trees
   * @param {authtree} refTree 
   * @param {authtree |
   *  authtree[]} allTrees 
   * @returns 
   */
  static Merge(refTree, allTrees){
    allTrees = ZArray.Ensure(allTrees);
    let allCtrls = _.map(allTrees, o => this.AuthTree2Ctrl(o));
    let merged = {};
    _.map(allCtrls, (o, i) => {
      merged = {
        ...merged,
        ...o
      };
    });
    return this.Ctrl2AuthTree(refTree, merged);
  }

  /**
   * Remove permissions from authtree
   * @param {authtree} oldtree 
   * @param {String | String[]} permissions //allow last * as wildcard
   * @returns 
   */
  static Remove(oldtree, permissions){
    let oldTreeCtrl = this.AuthTree2Ctrl(oldtree);
    let allKeys = Object.keys(oldTreeCtrl);
    let pathToBeRemoved = [];

    permissions = ZArray.Ensure(permissions);

    _.map(permissions, (o, i) => {
      if(o.slice(-1) === "*"){
        let prefix = o.slice(0, o.length - 2);
        let filtered = _.filter(allKeys, v => v.startsWith(prefix));
        pathToBeRemoved.push(...filtered);
      }else{
        pathToBeRemoved.push(o);
      }
    });
    _.map(oldTreeCtrl, (o, i) => {
      if(pathToBeRemoved.includes(i)){
        oldTreeCtrl[i] = undefined;
      }
    });
    let newTree = this.Ctrl2AuthTree(oldtree, oldTreeCtrl);
    return newTree;
  }

  /**
   * Grant permissions to authority tree
   * @param {authtree} refTree 
   * @param {authtree} oldtree 
   * @param {String | String[]} permissions //allow last * as wildcard
   * @returns 
   */
  static Grant(refTree, oldtree, permissions){
    let oldTreeCtrl = this.AuthTree2Ctrl(oldtree);
    let grantCtrl = this.Permissions2Ctrl(refTree, permissions);
    let mergedCtrl = {
      ...oldTreeCtrl,
      ...grantCtrl
    };

    let newTree = this.Ctrl2AuthTree(refTree, mergedCtrl);
    return newTree;
  }

  /**
   * 
   * translate permission paths to ctrl set
   * @param {authtree} refTree
   * @param {String | String[]} permissions //allow last * as wildcard
   */
  static Permissions2Ctrl(refTree, permissions){
    let authTreeCtrl = this.AuthTree2Ctrl(refTree);
    let allKeys = Object.keys(authTreeCtrl);

    permissions = ZArray.Ensure(permissions);
    let rtn = {};
    _.map(permissions, (o, i) => {
      let splited = o.split(".");
      let path = "";
      for(let j=0; j<splited.length; j++){
        let v = splited[j];
        if(v === "*"){
          let filtered = _.filter(allKeys, x => x.startsWith(path));
          _.map(filtered, (x, k) => {
            rtn[x] = true;
          });
        }else{
          path += (path === ""? "" : ".") + v;
          if(allKeys.includes(path)){
            rtn[path] = true;
          }else{
            break;
          }
        }
      }
    });
    return rtn;
  }

  /**
   * 
   * Parse authority tree to ctrl sets
   * @param {authtree} authtree 
   * @param {Boolean} defaultV 
   * @param {*} result  //for recursive
   * @param {String} level  //for recursive
   * @param {authctrlset} old  //for recursive
   * @returns 
   */
  static AuthTree2Ctrl(authtree, defaultV = true, result = null, level = "", old = null){
    if (!result) result = {};
    _.map(authtree, (o, i) => {
      if(_.isString(o)){
        let idx = level + "." + o;
        if(old && old[idx] !== undefined){
          result[idx] = old[idx];
        }else{
          result[idx] = defaultV;
        }
      }else{
        let nextlevel = level + (level === ""? i : ("." + i)); 
        if(old && old[nextlevel] !== undefined){
          result[nextlevel] = old[nextlevel];
        }else{
          result[nextlevel] = defaultV;
        }
        this.AuthTree2Ctrl(o, defaultV, result, nextlevel, old);
      }
    });
    return result;
  }

  /**
   * 
   * Parse ctrl set to authority tree with bounding of a reference tree
   * @param {authtree} refTree 
   * @param {authctrlset} ctrl 
   * @param {*} result //for recursive
   * @param {String} level  //for recursive
   * @returns 
   */
  static Ctrl2AuthTree(refTree, ctrl, result = null, level = ""){
    if (!result) result = {};
    if(_.isArray(refTree)){
      let funcs = [];
      _.map(refTree, (o, i) => {
        let idx = level + (level === ""? o : ("." + o));
        if(ctrl[idx] === true){
          funcs.push(o);
        }
      });
      Accessor.Set(result, level, funcs);
    }else{
      _.map(refTree, (o, i) => {
        let idx = level + (level === ""? i : ("." + i));
        if(ctrl[idx] === true){
          Accessor.Set(result, idx, {});
          this.Ctrl2AuthTree(o, ctrl, result, idx);
        }
      });
    }
    return result;
  }
  
}

export default AuthX;